/**
 * High-contrast ("View High Contrast Map") surround style.
 *
 * An accessibility-focused alternative to the default Positron style for
 * low-vision and color-blind users. It uses a bold, highly saturated palette
 * and thicker label halos so features are distinguishable by hue and remain
 * legible.
 *
 * It is a fully separate style file: it reuses the Positron layer skeleton via
 * `createPositronLayers`'s override hooks (so structural layer definitions are
 * not duplicated) but owns its own palette. The base Positron style and the
 * renderer have no knowledge of any of this.
 *
 * Textured fills (e.g. a grass pattern for parks) are intentionally out of scope
 * here and tracked as a separate follow-up PR; this style uses solid fills only.
 */

import { mergeRight } from 'ramda';

import { createPositronLayers, POSITRON_COLORS } from './positronStyle.ts';
import type { SurroundColors, SurroundStyle } from './surroundStyle.ts';

// Bold, saturated palette tuned for contrast against a warm light background.
const HIGH_CONTRAST_COLORS: SurroundColors = {
  background: '#FFFBF2',
  water: '#82cde8',
  park: '#8CBF5A',
  residential: 'rgb(234, 234, 230)',
  wood: 'rgb(210, 218, 210)',
  building: 'rgb(234, 234, 229)',
  buildingOutline: 'rgb(219, 219, 218)',
  roadCasing: '#C52E25',
  roadCasingMinor: '#BC4134',
  motorway: '#FCEFEC',
  majorRoad: '#FFF5F3',
  cityStreet: '#ffffff',
  roadMinor: '#A0A0A0',
  roadPath: '#A0A0A0',
  roadSubtle: 'hsla(0, 0%, 63%, 1)',
  railway: '#995AB3',
  railwayDash: '#995AB3',
  text: '#1A1A1A',
  textSubtle: '#1a1a1a',
  textHalo: '#FFFFFF',
  waterLabel: '#05455F',
  parkLabel: '#004808',
  aeroTaxiway: '#cc7905', // taxiway / taxilane centreline surface (mid)
  aeroRunway: '#cc7905', // runway / stopway surface (matches taxiway)
};

const combinedColors = mergeRight(POSITRON_COLORS, HIGH_CONTRAST_COLORS);

/** The high-contrast accessible surround style. */
const highContrastStyle: SurroundStyle = {
  id: 'highContrast',
  colors: combinedColors,
  createLayers: locale =>
    createPositronLayers(locale, {
      colors: combinedColors,
      // Solid, fully opaque fills for water and parks (no textured patterns).
      waterPaint: { 'fill-antialias': true, 'fill-color': HIGH_CONTRAST_COLORS.water, 'fill-opacity': 1 },
      parkPaint: { 'fill-antialias': true, 'fill-color': HIGH_CONTRAST_COLORS.park, 'fill-opacity': 1 },
      // Thicker halos keep labels legible over the busier high-contrast basemap.
      labelHaloWidth: 3,
    }),
};

export { HIGH_CONTRAST_COLORS };
export default highContrastStyle;
